import { Link, useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom'

/**
 * Friendly fallback for unmatched routes and route errors (used as the router
 * errorElement and the "*" catch-all), replacing React Router's dev error page.
 */
export function NotFound() {
  const error = useRouteError()
  const navigate = useNavigate()
  const isRouteError = isRouteErrorResponse(error)
  const status = isRouteError ? error.status : 404

  // Redirect to ServerError page for 5xx errors
  if (status >= 500) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
            <svg className="h-10 w-10 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </div>
          <div className="mb-2 text-5xl font-bold text-gray-900">{status}</div>
          <h1 className="mb-2 text-xl font-semibold text-gray-900">Server Error</h1>
          <p className="mb-6 text-gray-600">
            Something went wrong on our end. Please try again.
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
            >
              Go back
            </button>
            <Link
              to="/dashboard"
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Handle 403 Forbidden
  if (status === 403) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-yellow-100">
            <svg className="h-10 w-10 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div className="mb-2 text-5xl font-bold text-gray-900">403</div>
          <h1 className="mb-2 text-xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mb-6 text-gray-600">
            You don't have permission to access this page.
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
            >
              Go back
            </button>
            <Link
              to="/dashboard"
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Default 404 Not Found
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-blue-100">
          <svg className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="mb-2 text-5xl font-bold text-gray-900">404</div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">Page Not Found</h1>
        <p className="mb-6 text-gray-600">
          The page you're looking for doesn't exist or has been moved.
        </p>
        {import.meta.env.DEV && error !== null && error !== undefined && (
          <details className="mb-6 rounded-lg bg-gray-100 p-4 text-left">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Error details (dev only)
            </summary>
            <pre className="mt-2 overflow-auto text-xs text-gray-600">
              {isRouteError
                ? (error as { statusText?: string }).statusText
                : (error as Error)?.message || String(error)}
            </pre>
          </details>
        )}
        <div className="flex justify-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
          >
            Go back
          </button>
          <Link
            to="/dashboard"
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}
